import React, { useState, useMemo, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faChevronDown, faTrophy, faLocationDot } from '@fortawesome/free-solid-svg-icons';
import StandardsModal from './StandardsModal';
import { compareEvents, sortStrokeValues, sortDistances, parseEventName, } from '../../services/utils/EventOrdering';
import { STANDARD_CATEGORY_ORDER, getStandardCategory } from '../../services/utils/standardCategories';
import { parseEventKey } from '../../services/utils/keyParsing';
import { useCollapsible } from '../hooks/useCollapsible';
import { useCourseFilter, useGroupBy, groupLabel } from '../hooks/useEventFilters';
import { EventFilterBar, SingleFilterChipGroup, SegmentedToggle } from './filters';
const COURSE_FILTER_LABELS = { all: 'All', '50m': 'LC', '25m': 'SC' };
const GENDER_OPTIONS = [
    { value: 'Female', label: 'Female' },
    { value: 'Male', label: 'Male' },
];
const SELECTED_COUNTY_KEY = 'swim-lab:standards-viewer-county';
// Stable separator that won't appear in event names or course strings
const SEP = '\x00';
const categoryLabel = (category, names, countyTimesStore) => {
    const countries = [...new Set(names.map(n => countyTimesStore[n]?.country).filter(Boolean))];
    return countries.length === 1 ? `${category} — ${countries[0]}` : category;
};
const StandardsViewer = ({ countyTimesStore, defaultStandardNames, onClearOneCounty, onBack, onLoadCountyTimes }) => {
    const allCountyNames = Object.keys(countyTimesStore).sort();
    const [showStandardsModal, setShowStandardsModal] = useState(false);
    const groupedCounties = useMemo(() => {
        const groups = {};
        for (const name of allCountyNames) {
            const cat = getStandardCategory(countyTimesStore[name]?.countyName ?? name);
            if (!groups[cat])
                groups[cat] = [];
            groups[cat].push(name);
        }
        return STANDARD_CATEGORY_ORDER.flatMap(cat => groups[cat] ? [{ category: cat, names: groups[cat] }] : []);
    }, [allCountyNames]);
    const [selectedCounty, setSelectedCounty] = useState(() => {
        const saved = typeof window !== 'undefined' ? localStorage.getItem(SELECTED_COUNTY_KEY) : null;
        return saved || allCountyNames[0] || '';
    });
    const [selectedGender, setSelectedGender] = useState('Female');
    const [courseFilter, setCourseFilter] = useCourseFilter();
    useEffect(() => {
        if (allCountyNames.length === 0)
            return;
        setSelectedCounty(prev => prev && allCountyNames.includes(prev) ? prev : allCountyNames[0]);
    }, [allCountyNames]);
    useEffect(() => {
        if (typeof window !== 'undefined' && selectedCounty) {
            localStorage.setItem(SELECTED_COUNTY_KEY, selectedCounty);
        }
    }, [selectedCounty]);
    const [selectedStrokes, setSelectedStrokes] = useState([]);
    const [selectedDistances, setSelectedDistances] = useState([]);
    const [groupBy, setGroupBy] = useGroupBy();
    const [collapsedGroups, toggleGroup] = useCollapsible();
    const selectedEntry = countyTimesStore[selectedCounty];
    const allEntries = useMemo(() => {
        if (!selectedCounty || !selectedEntry)
            return [];
        const times = selectedEntry.times;
        const result = [];
        Object.entries(times).forEach(([key, entry]) => {
            const parsed = parseEventKey(key);
            if (!parsed)
                return;
            if (parsed.gender.toLowerCase() !== selectedGender.toLowerCase())
                return;
            const { stroke, distance } = parseEventName(parsed.event);
            result.push({
                event: parsed.event,
                course: parsed.course,
                stroke,
                distance: isFinite(distance) ? distance.toString() : '',
                ageCategory: entry.ageCategory,
                ageFrom: entry.ageFrom,
                entry,
            });
        });
        return result;
    }, [selectedCounty, selectedGender, countyTimesStore]);
    const availableStrokes = useMemo(() => sortStrokeValues(Array.from(new Set(allEntries.map(e => e.stroke)))), [allEntries]);
    const availableDistances = useMemo(() => sortDistances(Array.from(new Set(allEntries.map(e => e.distance).filter(Boolean)))), [allEntries]);
    const availableCourses = useMemo(() => [...new Set(allEntries.map(e => e.course))].sort().reverse(), [allEntries]);
    const courseFilterOptions = useMemo(() => ['all', '50m', '25m'].filter(c => c === 'all' || availableCourses.includes(c)), [availableCourses]);
    const filteredEntries = useMemo(() => allEntries.filter(e => {
        const courseMatch = courseFilter === 'all' || e.course === courseFilter;
        const strokeMatch = selectedStrokes.length === 0 || selectedStrokes.includes(e.stroke);
        const distMatch = selectedDistances.length === 0 || selectedDistances.includes(e.distance);
        return courseMatch && strokeMatch && distMatch;
    }), [allEntries, courseFilter, selectedStrokes, selectedDistances]);
    // showCourseBadge: only needed when multiple courses appear in the same group
    const showCourseBadge = groupBy === 'stroke' && courseFilter === 'all' && availableCourses.length > 1;
    const groups = useMemo(() => {
        const groupMap = new Map();
        // groupMap: groupKey -> rowKey -> ageCategory -> entry
        filteredEntries.forEach(e => {
            const groupKey = groupBy === 'course' ? e.course : e.stroke;
            const rowKey = `${e.event}${SEP}${e.course}`;
            if (!groupMap.has(groupKey))
                groupMap.set(groupKey, new Map());
            const rowMap = groupMap.get(groupKey);
            if (!rowMap.has(rowKey))
                rowMap.set(rowKey, new Map());
            rowMap.get(rowKey).set(e.ageCategory, e.entry);
        });
        const sortedGroupKeys = groupBy === 'course'
            ? [...groupMap.keys()].sort().reverse() // 50m first
            : sortStrokeValues([...groupMap.keys()]);
        return sortedGroupKeys.map(groupKey => {
            const rowMap = groupMap.get(groupKey);
            // Collect all age categories across all rows in this group
            const ageCatOrder = new Map();
            rowMap.forEach(ageCellMap => {
                ageCellMap.forEach((entry, ageCat) => ageCatOrder.set(ageCat, entry.ageFrom));
            });
            const ageColumns = [...ageCatOrder.entries()]
                .sort((a, b) => a[1] - b[1])
                .map(([cat]) => cat);
            // Sort rows by event ordering then course (50m before 25m)
            const sortedRowKeys = [...rowMap.keys()].sort((a, b) => {
                const [evA, crsA] = a.split(SEP);
                const [evB, crsB] = b.split(SEP);
                const evCmp = compareEvents(evA, evB);
                return evCmp !== 0 ? evCmp : crsB.localeCompare(crsA);
            });
            return { groupKey, ageColumns, sortedRowKeys, rowMap };
        });
    }, [filteredEntries, groupBy]);
    const stickyCol = { position: 'sticky', left: 0, zIndex: 1 };
    return (React.createElement("div", { className: "tab-content active" },
        React.createElement("div", { style: { display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.25rem' } },
            React.createElement("button", { className: "btn-ghost section-toggle", onClick: onBack, title: "Back to home" },
                React.createElement(FontAwesomeIcon, { icon: faArrowLeft }),
                " Back"),
            React.createElement("h2", { style: { margin: 0, fontSize: '1.2rem' } },
                "Qualification Times",
                selectedEntry ? ` — ${selectedEntry.country} ${selectedEntry.year}` : ''),
            React.createElement("button", { className: "btn-ghost", onClick: () => setShowStandardsModal(true), title: "Manage Standards", style: { color: 'var(--primary)', padding: '4px 8px', marginLeft: 'auto' } }, "Manage\u2026")),
        showStandardsModal && (React.createElement(StandardsModal, { countyTimesStore: countyTimesStore, defaultStandardNames: defaultStandardNames, onClearOneCounty: onClearOneCounty, onLoadCountyTimes: onLoadCountyTimes, onClose: () => setShowStandardsModal(false) })),
        React.createElement("div", { className: "standards-viewer-controls", style: { display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '0.75rem', alignItems: 'center' } },
            React.createElement("div", { className: "rankings-county-filter", style: { flex: 1 } },
                React.createElement(FontAwesomeIcon, { icon: faLocationDot, className: "rankings-county-icon" }),
                React.createElement("select", { value: selectedCounty, onChange: e => setSelectedCounty(e.target.value), className: "rankings-county-select" }, groupedCounties.map(({ category, names }) => (React.createElement("optgroup", { key: category, label: categoryLabel(category, names, countyTimesStore) }, names.map(name => (React.createElement("option", { key: name, value: name }, countyTimesStore[name] ? `${countyTimesStore[name].countyName} (${countyTimesStore[name].year})` : name)))))))),
            React.createElement(SegmentedToggle, { options: GENDER_OPTIONS, value: selectedGender, onChange: setSelectedGender })),
        allEntries.length > 0 && (React.createElement(EventFilterBar, { strokes: availableStrokes, selectedStrokes: selectedStrokes, onStrokesChange: setSelectedStrokes, distances: availableDistances, selectedDistances: selectedDistances, onDistancesChange: setSelectedDistances, groupBy: groupBy, onGroupByChange: setGroupBy, hideSingleOptions: true, extraFilters: availableCourses.length > 1 && (React.createElement(SingleFilterChipGroup, { label: "Course", options: courseFilterOptions, value: courseFilter, onChange: setCourseFilter, renderLabel: c => COURSE_FILTER_LABELS[c] })) })),
        allCountyNames.length === 0 ? (React.createElement("div", { style: { padding: '2rem', textAlign: 'center', color: 'var(--text-secondary)' } },
            "No standards loaded yet. Click ",
            React.createElement("strong", null, "Load from File"),
            " to import qualification times.")) : filteredEntries.length === 0 ? (React.createElement("div", { style: { padding: '2rem', textAlign: 'center', color: 'var(--text-secondary)' } },
            "No times found for ",
            selectedGender,
            " with the current filters.")) : (groups.map(({ groupKey, ageColumns, sortedRowKeys, rowMap }, groupIndex) => (React.createElement("div", { key: groupKey, className: `club-group${groupIndex > 0 ? ' club-group-spaced' : ''}` },
            React.createElement("div", { className: "club-header", onClick: () => toggleGroup(groupKey) },
                React.createElement("h3", { className: "club-header-title" },
                    React.createElement(FontAwesomeIcon, { icon: faTrophy }),
                    " ",
                    groupLabel(groupBy, groupKey)),
                React.createElement(FontAwesomeIcon, { icon: faChevronDown, className: `chevron-icon${!collapsedGroups.has(groupKey) ? ' expanded' : ''}` })),
            !collapsedGroups.has(groupKey) && (React.createElement("div", { className: "table-container" },
                React.createElement("table", null,
                    React.createElement("thead", null,
                        React.createElement("tr", null,
                            React.createElement("th", { style: { textAlign: 'left', minWidth: '140px' } }, "Event"),
                            ageColumns.map(age => (React.createElement("th", { key: age, style: { textAlign: 'center', minWidth: '72px', whiteSpace: 'nowrap' } }, age))))),
                    React.createElement("tbody", null, sortedRowKeys.map(rowKey => {
                        const [event, course] = rowKey.split(SEP);
                        const ageCellMap = rowMap.get(rowKey);
                        return (React.createElement("tr", { key: rowKey },
                            React.createElement("td", { className: "card-event-td", style: { ...stickyCol, fontWeight: 600 } },
                                event,
                                showCourseBadge && (React.createElement("span", { className: "swimmer-tag-badge", style: { marginLeft: '0.4em' } }, course === '50m' ? 'LC' : 'SC'))),
                            ageColumns.map(age => {
                                const entry = ageCellMap.get(age);
                                return (React.createElement("td", { key: age, "data-label": age, style: { textAlign: 'center', whiteSpace: 'nowrap' } }, entry ? (React.createElement("div", null,
                                    React.createElement("div", { style: { fontWeight: 500 } }, entry.time),
                                    entry.considerationTime && (React.createElement("div", { style: { fontSize: '0.82em', color: 'var(--text-secondary)', marginTop: '1px' } }, entry.considerationTime)))) : (React.createElement("span", { style: { color: 'var(--text-secondary)' } }, "\u2014"))));
                            })));
                    }))))))))),
        filteredEntries.length > 0 && (React.createElement("div", { style: { marginTop: '0.75rem', fontSize: '0.82em', color: 'var(--text-secondary)' } },
            "Automatic qualifying time / ",
            React.createElement("span", { style: { opacity: 0.7 } }, "consideration time")))));
};
export default StandardsViewer;
